import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { API } from "@/lib/auth";
import ExchangeAlliancePage from "@/pages/clientDemos/ExchangeAlliancePage";

const BASE = process.env.REACT_APP_BACKEND_URL;
// Demos with a public, code-free preview route, and the only sites allowed to embed them.
const PREVIEW_DEMOS = { "exchange-alliance": ["hoh2life.com"] };

function embedAllowed(domains) {
  if (window.self === window.top) return true;
  const parent = window.location.ancestorOrigins?.[0] || document.referrer || "";
  if (!parent) return true;
  try {
    const host = new URL(parent).hostname;
    return host === window.location.hostname || domains.some((d) => host === d || host.endsWith(`.${d}`));
  } catch { return false; }
}

function Notice({ text }) {
  return (
    <section data-testid="demo-preview-blocked" className="min-h-screen bg-[#0A0612] flex items-center justify-center px-6">
      <p className="text-slate-400 font-mono text-sm tracking-[0.2em] uppercase text-center">{text}</p>
    </section>
  );
}

function TapCover({ slug, onBegin }) {
  const [intro, setIntro] = useState(null);
  useEffect(() => { axios.get(`${API}/demos/${slug}/intro`).then(({ data }) => setIntro(data)).catch(() => setIntro({})); }, [slug]);
  return (
    <section data-testid="demo-preview-cover" className="min-h-screen bg-[#0A0612] text-white flex items-center justify-center px-6 relative overflow-hidden">
      <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 45%, rgba(212,175,55,0.18), transparent 60%)" }} />
      <motion.div className="relative text-center" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        {intro?.logo_url && (
          <img src={`${BASE}${intro.logo_url}`} alt="The Exchange Alliance" data-testid="demo-preview-logo"
            className="mx-auto w-36 h-36 sm:w-44 sm:h-44 rounded-full object-cover ring-2 ring-[#D4AF37]/60 shadow-[0_0_60px_rgba(212,175,55,0.35)]" />
        )}
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight bg-gradient-to-b from-[#FFF4CC] via-[#F4D88A] to-[#C9A227] bg-clip-text text-transparent">
          The Exchange Alliance
        </h1>
        <p className="mt-3 text-slate-300">Sound on — see how one Life Pass moves people from crisis to self-sufficiency.</p>
        <motion.button type="button" onClick={onBegin} data-testid="demo-preview-begin"
          className="mt-10 inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#C9A227] via-[#F4D88A] to-[#C9A227] px-10 py-5 text-lg font-extrabold text-[#0A0612] hover:brightness-110 active:scale-95 transition-[filter,transform]"
          animate={{ boxShadow: ["0 0 0 0 rgba(244,216,138,0.6)", "0 0 0 24px rgba(244,216,138,0)"] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <Play className="w-5 h-5 fill-current" /> Tap to begin
        </motion.button>
      </motion.div>
    </section>
  );
}

// `/demo/:slug/preview` — code-free embed route (Exchange Alliance only). The /demos/:slug/unlock flow is untouched.
export default function DemoPreviewPage() {
  const { slug } = useParams();
  const [started, setStarted] = useState(false);
  const domains = PREVIEW_DEMOS[slug];
  if (!domains) return <Notice text="Preview not available" />;
  if (!embedAllowed(domains)) return <Notice text={`This demo can only be embedded on ${domains[0]}`} />;
  if (!started) return <TapCover slug={slug} onBegin={() => setStarted(true)} />;
  return (
    <section data-testid="demo-preview-page" className="min-h-screen text-slate-100 tea-theme bg-[#0A0612]">
      <div className="py-6 px-4 md:px-10">
        <div className="max-w-7xl mx-auto">
          <ExchangeAlliancePage slug={slug} embed autoStart />
        </div>
      </div>
    </section>
  );
}
